// Human-style logical solver. Returns the easiest applicable `SolveStep`
// per `TECHNIQUE_ORDER`. Elimination-only techniques mutate the
// `CandidateState` carried across iterations; placement steps write the grid
// and force a state rebuild. Task 7 (X-wing) appends one more variant.

import {
  BOARD_SIZE,
  CELL_COUNT,
  cloneGrid,
  type Digit,
  type Grid,
  type NonEmptyDigit,
} from './types.js';
import {
  bit,
  buildCandidateState,
  cellsOfUnit,
  eliminateCandidate,
  lowestDigit,
  popcount,
  type CandidateState,
  type Unit,
  type UnitKind,
} from './Candidates.js';
import {
  findBoxLineReduction,
  findNakedPair,
  findPointingPair,
  type BoxLineStep,
  type NakedPairStep,
} from './SolverTechniques.js';
import { findXWing, type XWingStep } from './XWingTechnique.js';

export type { Unit, UnitKind } from './Candidates.js';
export type { BoxLineStep, Elimination, NakedPairStep } from './SolverTechniques.js';
export type { XWingStep } from './XWingTechnique.js';

export type Technique = 'naked-single' | 'hidden-single' | 'naked-pair' | 'pointing-pair' | 'box-line' | 'x-wing';

export interface NakedSingleStep {
  readonly technique: 'naked-single';
  readonly cellIndex: number;
  readonly digit: NonEmptyDigit;
}

export interface HiddenSingleStep {
  readonly technique: 'hidden-single';
  readonly cellIndex: number;
  readonly digit: NonEmptyDigit;
  readonly unit: Unit;
}

export interface PointingPairStep extends Omit<BoxLineStep, 'technique' | 'direction'> {
  readonly technique: 'pointing-pair';
  readonly direction: 'box-to-line';
}

export interface BoxLineReductionStep extends Omit<BoxLineStep, 'technique' | 'direction'> {
  readonly technique: 'box-line';
  readonly direction: 'line-to-box';
}

export type SolveStep = NakedSingleStep | HiddenSingleStep | NakedPairStep | PointingPairStep | BoxLineReductionStep | XWingStep;

const TECHNIQUE_ORDER: readonly Technique[] = ['naked-single', 'hidden-single', 'naked-pair', 'pointing-pair', 'box-line', 'x-wing'];

export const TECHNIQUE_RANK: Readonly<Record<Technique, number>> = Object.freeze(
  Object.fromEntries(TECHNIQUE_ORDER.map((t, i) => [t, i])) as Record<Technique, number>,
);

function findNakedSingle(state: CandidateState, grid: Grid): NakedSingleStep | null {
  for (let i = 0; i < CELL_COUNT; i++) {
    if ((grid[i] ?? 0) !== 0) continue;
    const mask = state.cells[i] ?? 0;
    if (popcount(mask) === 1) {
      return { technique: 'naked-single', cellIndex: i, digit: lowestDigit(mask) };
    }
  }
  return null;
}

function findHiddenSingleInUnit(
  unit: Unit,
  state: CandidateState,
  grid: Grid,
): HiddenSingleStep | null {
  const cells = cellsOfUnit(unit);
  for (let n = 1; n <= BOARD_SIZE; n++) {
    const d = n as NonEmptyDigit;
    const b = bit(d);
    let count = 0;
    let where = -1;
    for (const i of cells) {
      if ((grid[i] ?? 0) !== 0) continue;
      if (((state.cells[i] ?? 0) & b) !== 0) {
        count++;
        where = i;
        if (count > 1) break;
      }
    }
    if (count === 1 && where !== -1) {
      return { technique: 'hidden-single', cellIndex: where, digit: d, unit };
    }
  }
  return null;
}

function findHiddenSingle(state: CandidateState, grid: Grid): HiddenSingleStep | null {
  const kinds: UnitKind[] = ['row', 'col', 'box'];
  for (const kind of kinds) {
    for (let idx = 0; idx < BOARD_SIZE; idx++) {
      const step = findHiddenSingleInUnit({ kind, index: idx }, state, grid);
      if (step !== null) return step;
    }
  }
  return null;
}

function tagBoxLine(s: BoxLineStep): PointingPairStep | BoxLineReductionStep {
  const { box, line, digit, anchorCells, eliminations } = s;
  return s.direction === 'box-to-line'
    ? { technique: 'pointing-pair', direction: 'box-to-line', box, line, digit, anchorCells, eliminations }
    : { technique: 'box-line', direction: 'line-to-box', box, line, digit, anchorCells, eliminations };
}

function nextStepFrom(grid: Grid, state: CandidateState): SolveStep | null {
  const ns = findNakedSingle(state, grid);
  if (ns !== null) return ns;
  const hs = findHiddenSingle(state, grid);
  if (hs !== null) return hs;
  const np = findNakedPair(state);
  if (np !== null) return np;
  const pp = findPointingPair(state);
  if (pp !== null) return tagBoxLine(pp);
  const bl = findBoxLineReduction(state);
  if (bl !== null) return tagBoxLine(bl);
  const xw = findXWing(state);
  if (xw !== null) return xw;
  return null;
}

/** Easiest applicable step for `grid`, or `null` if solved/stuck/invalid. */
export function nextStep(grid: Grid): SolveStep | null {
  const state = buildCandidateState(grid);
  if (state === null) return null;
  return nextStepFrom(grid, state);
}

export interface SolveResult {
  readonly solved: boolean;
  readonly steps: SolveStep[];
  readonly finalGrid: Grid;
}

/** Loops until solved or stuck. Does not mutate the input. */
export function solve(grid: Grid): SolveResult {
  const working = cloneGrid(grid);
  let state = buildCandidateState(working);
  if (state === null) return { solved: false, steps: [], finalGrid: working };
  const steps: SolveStep[] = [];
  for (;;) {
    const step = nextStepFrom(working, state);
    if (step === null) break;
    const placed = applyStep(working, state, step);
    steps.push(step);
    if (placed) {
      const rebuilt = buildCandidateState(working);
      if (rebuilt === null) break;
      state = rebuilt;
    }
  }
  return { solved: isComplete(working), steps, finalGrid: working };
}

// Returns true iff a digit was placed (caller rebuilds candidates).
function applyStep(grid: Grid, state: CandidateState, step: SolveStep): boolean {
  if (step.technique === 'naked-single' || step.technique === 'hidden-single') {
    grid[step.cellIndex] = step.digit as Digit;
    return true;
  }
  for (const e of step.eliminations) eliminateCandidate(state, e.cellIndex, bit(e.digit));
  return false;
}

function isComplete(grid: Grid): boolean {
  for (let i = 0; i < CELL_COUNT; i++) {
    if ((grid[i] ?? 0) === 0) return false;
  }
  return true;
}
